// AsyncCustomSelect.tsx
import classNames from 'classnames';
import React from 'react';
import { GroupBase, StylesConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { SelectOptions } from '../../utils/types/react_select_types';
type AsyncCustomSelectProps = {
  className?: string;
  name?: string;
  label?: string;
  value?: SelectOptions | SelectOptions[];
  defaultOptions: boolean | SelectOptions[] | undefined;
  onChange: any;
  isMulti?: boolean;
  disabled?: boolean;
  loadOptions: (inputValue: string, callback: (options: any) => void) => void;
  onInputChange?: any;
  placeholder?: string;
  creatable?: boolean;
  onCreateOption?: (inputValue: string) => void;
  components?: any;
};

const customStyles: StylesConfig<
  SelectOptions,
  boolean,
  GroupBase<SelectOptions>
> = {
  // ensure dropdown sits above other overlays
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e2e8f0',
  }),

  // ── Control (the input box) ───────────────────────────────────────────────────
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: state.isDisabled ? '#f8fafc' : '#ffffff',
    borderColor: state.isFocused ? '#10b981' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(16, 185, 129, 0.1)' : 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: state.isFocused ? '#10b981' : '#cbd5e1',
    },
  }),

  // ── Placeholder text ───────────────────────────────────────────────────────────
  placeholder: (base) => ({
    ...base,
    color: '#94a3b8',
    fontSize: '14px',
    marginLeft: '0.25rem',
  }),

  // ── Typed text ────────────────────────────────────────────────────────────────
  input: (base) => ({
    ...base,
    margin: 0,
    padding: '0 0.25rem',
    color: '#475569',
    fontSize: '14px',
  }),

  // ── Selected value ────────────────────────────────────────────────────────────
  singleValue: (base) => ({
    ...base,
    margin: '0 0.25rem',
    color: '#475569',
    fontSize: '14px',
    fontWeight: '500',
  }),

  // ── Remove extra padding around value ─────────────────────────────────────────
  valueContainer: (base) => ({
    ...base,
    padding: '2px 8px',
  }),

  // ── Hide the separator line ───────────────────────────────────────────────────
  indicatorSeparator: () => ({ display: 'none' }),

  // ── Chevron & clear icons ─────────────────────────────────────────────────────
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 0.5rem',
    color: '#94a3b8',
    cursor: 'pointer',
    '&:hover': {
      color: '#64748b',
    },
    svg: {
      width: '16px',
      height: '16px',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '0 0.5rem',
    svg: {
      width: '16px',
      height: '16px',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#f0fdf4'
      : state.isFocused
        ? '#f8fafc'
        : 'transparent',
    color: state.isSelected ? '#059669' : '#475569',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '8px 12px',
    textAlign: 'left',
    ':active': {
      backgroundColor: '#f0fdf4',
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#ecfdf5',
    borderRadius: '9999px',
    fontSize: '11px',
    margin: '2px',
    border: '1px solid #d1fae5',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#059669',
    padding: '2px 8px',
    fontWeight: '600',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#10b981',
    borderRadius: '9999px',
    ':hover': {
      backgroundColor: '#10b981',
      color: '#ffffff',
    },
  }),
};

const AsyncCustomSelect = ({
  name = '',
  value,
  label,
  defaultOptions,
  loadOptions,
  onChange,
  onInputChange,
  disabled = false,
  isMulti = false,
  placeholder = 'Search …',
  className,
  creatable = false,
  onCreateOption,
  components,
}: AsyncCustomSelectProps) => {
  const SelectComponent = creatable ? AsyncCreatableSelect : AsyncSelect;

  return (
    <div className={classNames('w-full', className)}>
      { label && (
        <label
            htmlFor={name}
            className='block mb-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-0.5'
        >
          { label }
        </label>
      ) }
      <SelectComponent
          classNamePrefix='react-select'
          name={name}
          value={value}
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          onChange={onChange}
          onInputChange={onInputChange}
          isDisabled={disabled}
          isMulti={isMulti}
          isSearchable={!disabled}
          placeholder={placeholder}
          menuPortalTarget={
            typeof document !== 'undefined' ? document.body : undefined
          }
          styles={customStyles}
          menuPosition='fixed'
          maxMenuHeight={200}
          isClearable
          cacheOptions
          loadingMessage={() => 'Loading…'}
          noOptionsMessage={() => 'No options'}
          components={components}
          {...(creatable && onCreateOption ? { onCreateOption } : {})}
      />
    </div>
  );
};

export default AsyncCustomSelect;
